// Multi-select searchable material picker for Part-use member add.
// Checkboxes on filtered rows + "Select all filtered" + commit button.
// Search uses comma-AND phrases (same as Inventory / Models toolbars).
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { mfgMdInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  haystackMatchesCommaSearch,
  parseCommaDelimitedSearchTerms,
} from "@/lib/manufacturing/commaDelimitedSearch";

const LIMIT = 80;

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

function score(material: MfgMaterial, firstTerm: string): number {
  if (!firstTerm) return 1;
  const code = normalize(material.code);
  const name = normalize(material.name);
  if (code === firstTerm || name === firstTerm) return 1000;
  if (code.startsWith(firstTerm)) return 500;
  if (name.startsWith(firstTerm)) return 400;
  if (code.includes(firstTerm)) return 200;
  if (name.includes(firstTerm)) return 100;
  return 1;
}

function filterMaterials(
  materials: ReadonlyArray<MfgMaterial>,
  query: string,
): MfgMaterial[] {
  const terms = parseCommaDelimitedSearchTerms(query);
  const first = terms[0] ?? "";
  const matched = materials.filter((m) =>
    haystackMatchesCommaSearch(`${m.code} ${m.name}`, query),
  );
  if (!first) return matched.slice(0, LIMIT);
  return matched
    .map((m) => ({ m, s: score(m, first) }))
    .sort((a, b) => b.s - a.s || a.m.name.localeCompare(b.m.name))
    .slice(0, LIMIT)
    .map((x) => x.m);
}

type Props = {
  materials: ReadonlyArray<MfgMaterial>;
  onAdd: (materials: MfgMaterial[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

export function MaterialSearchableMultiSelect({
  materials,
  onAdd,
  placeholder = "Search plant materials — commas AND (e.g. hinge, brass)…",
  disabled,
}: Props) {
  const activeMaterials = useMemo(
    () => materials.filter((m) => m.is_active),
    [materials],
  );
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [panel, setPanel] = useState<{
    left: number;
    width: number;
    top: number;
    maxHeight: number;
  } | null>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanel(null);
      return;
    }
    function place() {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewTop = vv?.offsetTop ?? 0;
      const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const margin = 8;
      const gap = 4;
      const below = viewBottom - rect.bottom - margin;
      const above = rect.top - viewTop - margin;
      const openUp = below < 220 && above > below;
      const maxHeight = Math.max(140, Math.min(320, (openUp ? above : below) - gap));
      const top = openUp ? Math.max(viewTop + margin, rect.top - gap - maxHeight) : rect.bottom + gap;
      setPanel({
        left: Math.max(margin, rect.left),
        width: Math.min(rect.width, window.innerWidth - margin * 2),
        top,
        maxHeight,
      });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open]);

  const filtered = useMemo(
    () => filterMaterials(activeMaterials, query),
    [activeMaterials, query],
  );

  const filteredSelectedCount = filtered.filter((m) => selectedIds.has(m.id)).length;
  const allFilteredSelected =
    filtered.length > 0 && filteredSelectedCount === filtered.length;

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const m of filtered) next.add(m.id);
      return next;
    });
  }

  function clearFilteredSelection() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const m of filtered) next.delete(m.id);
      return next;
    });
  }

  function commit() {
    const picks = activeMaterials.filter((m) => selectedIds.has(m.id));
    if (picks.length === 0) return;
    onAdd(picks);
    setSelectedIds(new Set());
    setQuery("");
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative space-y-2">
      <input
        ref={inputRef}
        type="text"
        className={mfgMdInputClass}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        aria-autocomplete="list"
        aria-expanded={open}
        role="combobox"
      />
      {open && panel
        ? createPortal(
        <div
          ref={panelRef}
          className="fixed z-50 flex flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          style={{ left: panel.left, top: panel.top, width: panel.width, height: panel.maxHeight }}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-2 py-1.5 dark:border-zinc-800">
            <button
              type="button"
              className="text-[11px] font-medium text-indigo-700 disabled:opacity-40 dark:text-indigo-300"
              disabled={disabled || filtered.length === 0}
              onClick={() =>
                allFilteredSelected ? clearFilteredSelection() : selectAllFiltered()
              }
            >
              {allFilteredSelected ? "Clear filtered" : "Select all filtered"}
              {filtered.length > 0 ? ` (${filtered.length})` : ""}
            </button>
            <span className="text-[11px] text-gray-500 dark:text-zinc-400">
              {selectedIds.size} selected
            </span>
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500 dark:text-zinc-400">
              No matching materials.
            </div>
          ) : (
            <ul role="listbox" aria-multiselectable className="min-h-0 flex-1 overflow-auto">
              {filtered.map((m) => {
                const checked = selectedIds.has(m.id);
                return (
                  <li key={m.id}>
                    <label
                      title={`${m.code} — ${m.name}`}
                      className={
                        "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm " +
                        (checked
                          ? "bg-indigo-50 dark:bg-zinc-800"
                          : "hover:bg-indigo-50/70 dark:hover:bg-zinc-800/70")
                      }
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 shrink-0"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(m.id)}
                      />
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-gray-900 dark:text-zinc-100">
                          {m.code}
                        </span>
                        <span className="text-gray-500 dark:text-zinc-400"> — {m.name}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="shrink-0 border-t border-gray-100 px-2 py-1.5 dark:border-zinc-800">
            <button
              type="button"
              className="w-full rounded-md bg-indigo-700 px-2 py-1.5 text-xs font-semibold text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disabled || selectedIds.size === 0}
              onClick={commit}
            >
              Add {selectedIds.size || ""} selected
            </button>
          </div>
        </div>,
        document.body,
      )
        : null}
    </div>
  );
}
